import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch } from 'react-icons/fi';
import PostsShellSidebar from './PostsShellSidebar';
import AppShellSidebar from './AppShellSidebar';
import PostsShellHeaderAuth from './PostsShellHeaderAuth';
import PostsMobileGroupedShellChrome from './PostsMobileGroupedShellChrome';
import ShellHeaderProductsNav from '../../../../../layout/ShellHeaderProductsNav';
import { APP_NAME, LOGO_ICON_SRC } from '../../../../../config/branding';
import { createResizeObserver } from '../../../../../utils/resizeObserver';
import '../../styles/OpenDiscussions.css';

/**
 * Reusable posts shell: header + left sidebar + main content slot.
 * Matches the layout used on /posts (support-open-disc-shell-*).
 */
export default function PostsShellLayout({
  children,
  layoutClassName = '',
  mainClassName = 'posts-page-layout__center',
  shell = null,
  sidebar = null,
  sidebarItems = null,
  sidebarProps = {},
  returnTo,
  searchPlaceholder = 'Search posts, people, and more...',
  showHeaderSearch = true,
  searchValue,
  onSearchChange,
  onSearchKeyDown,
  onManageProfileClick,
  onGuestSignUp,
  onGuestSignIn,
  appShell = false,
  groupedMobileTools = false,
  mobileSearchInContext = false,
  onMobileSearchGo,
  notificationsPageActive = false,
  messagesPageActive = false,
  messagesStandalone = false,
}) {
  const shellHeaderRef = useRef(null);
  const {
    baseUrl,
    profile,
    profileAvatarSrc,
    isGuestViewer,
    themeMode,
    setThemeMode,
    headerDropdowns,
    setHeaderDropdowns,
    toggleHeaderDropdown,
    isMobileLayout,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    isNavCollapsed,
    feedSearchQ,
    setFeedSearchQ,
    messagingRef,
    notificationsRef,
    profileRef,
    shellRootRef,
    resolvedReturnTo,
    shellNavLayoutReady,
    shellNavLabel,
    shellNavIcon,
    toggleShellNav,
    defaultSidebarProps,
    handleSearchKeyDown: shellSearchKeyDown,
    closeDrawer,
  } = shell;

  const resolvedSearchValue = searchValue ?? feedSearchQ;
  const handleSearchChange = onSearchChange ?? ((e) => setFeedSearchQ(e.target.value));
  const handleSearchKeyDown = onSearchKeyDown ?? shellSearchKeyDown;

  const mergedSidebarProps = { ...defaultSidebarProps, ...sidebarProps };

  const customSidebarDrawerProps = {
    drawerOpenClass: mergedSidebarProps.drawerOpenClass,
    ariaHidden: mergedSidebarProps.ariaHidden,
    onItemNavigate: closeDrawer,
  };

  const shellSidebar =
    sidebar != null
      ? React.isValidElement(sidebar)
        ? React.cloneElement(sidebar, customSidebarDrawerProps)
        : sidebar
      : Array.isArray(sidebarItems) && sidebarItems.length > 0
        ? (
            <AppShellSidebar
              items={sidebarItems}
              isNavCollapsed={isNavCollapsed}
              drawerOpenClass={mergedSidebarProps.drawerOpenClass}
              ariaHidden={mergedSidebarProps.ariaHidden}
              ariaLabel={mergedSidebarProps.ariaLabel || 'App navigation'}
              onItemNavigate={closeDrawer}
            />
          )
        : (
            <PostsShellSidebar {...mergedSidebarProps} />
          );

  const useMobileSingleRow = isMobileLayout && !showHeaderSearch;
  const handleMobileChromeCloseDrawer = useCallback(
    (e) => {
      if (!mobileDrawerOpen) return;
      if (e.target.closest('.shell-header-products-nav, .products-nav-menu, [data-products-nav-menu-layer]')) return;
      if (e.target.closest('.support-open-disc-shell-header-left .support-open-disc-shell-header-btn')) return;
      if (
        e.target.closest(
          '.support-open-disc-shell-header-icon, .support-open-disc-shell-header-profile, .support-open-disc-shell-header-theme, .support-open-disc-shell-header-right button'
        )
      ) {
        return;
      }
      closeDrawer();
    },
    [mobileDrawerOpen, closeDrawer]
  );
  const headerClassName = [
    'support-open-disc-shell-header',
    !showHeaderSearch ? 'support-open-disc-shell-header--no-search' : '',
    useMobileSingleRow ? 'support-open-disc-shell-header--mobile-single' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const headerRightClassName = [
    'support-open-disc-shell-header-right',
    useMobileSingleRow ? 'support-open-disc-shell-header-right--mobile-tools' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const shellHeader = (
    <header className={headerClassName}>
      <div className="support-open-disc-shell-header-left">
        <ShellHeaderProductsNav />
        <button
          type="button"
          className="support-open-disc-shell-header-btn noOutline-text"
          onClick={shellNavLayoutReady ? toggleShellNav : undefined}
          aria-label={shellNavLabel}
          aria-expanded={isMobileLayout ? mobileDrawerOpen : undefined}
          disabled={!shellNavLayoutReady && !isMobileLayout}
        >
          {shellNavIcon}
        </button>
        <img src={LOGO_ICON_SRC} alt="Logo" className="support-open-disc-shell-logo-image" />
        <div className="support-open-disc-shell-logo-text">{APP_NAME}</div>
      </div>
      {showHeaderSearch && !isMobileLayout ? (
        <div className="support-open-disc-shell-header-search">
          <div className="support-open-disc-search support-open-disc-search--hero support-open-disc-search--header">
            <FiSearch className="support-open-disc-search-icon" aria-hidden />
            <input
              type="search"
              value={resolvedSearchValue}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              aria-label="Search posts"
            />
            <img src={LOGO_ICON_SRC} alt="Logo" className="support-open-disc-search-logo-image" />
          </div>
        </div>
      ) : null}
      <div className={headerRightClassName}>
        <PostsShellHeaderAuth
          isGuestViewer={isGuestViewer}
          returnTo={returnTo ?? resolvedReturnTo}
          onGuestSignUp={onGuestSignUp}
          onGuestSignIn={onGuestSignIn}
          themeMode={themeMode}
          onToggleTheme={() => setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'))}
          headerDropdowns={headerDropdowns}
          onToggleHeaderDropdown={toggleHeaderDropdown}
          messagingRef={messagingRef}
          notificationsRef={notificationsRef}
          profileRef={profileRef}
          profileAvatarSrc={profileAvatarSrc}
          profileUserId={profile?.userId}
          profileDisplayName={profile?.displayName || profile?.name}
          profileMode={profile?.profileMode || 'personal'}
          baseUrl={baseUrl}
          onNotificationsNavigateAll={() =>
            setHeaderDropdowns((prev) => ({ ...prev, notifications: false }))
          }
          onManageProfileClick={onManageProfileClick}
          messagesStandalone={messagesStandalone}
        />
      </div>
    </header>
  );

  const mobileChrome = isMobileLayout ? (
    groupedMobileTools ? (
      <PostsMobileGroupedShellChrome
        shellHeaderRef={shellHeaderRef}
        shellNavIcon={shellNavIcon}
        shellNavLabel={shellNavLabel}
        shellNavLayoutReady={shellNavLayoutReady}
        toggleShellNav={toggleShellNav}
        mobileDrawerOpen={mobileDrawerOpen}
        setMobileDrawerOpen={setMobileDrawerOpen}
        feedSearchQ={resolvedSearchValue}
        setFeedSearchQ={setFeedSearchQ}
        onSearchChange={handleSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        mobileSearchInContext={mobileSearchInContext}
        onMobileSearchGo={onMobileSearchGo}
        themeMode={themeMode}
        setThemeMode={setThemeMode}
        isGuestViewer={isGuestViewer}
        profile={profile}
        profileAvatarSrc={profileAvatarSrc}
        returnTo={returnTo ?? resolvedReturnTo}
        onGuestSignUp={onGuestSignUp}
        onManageProfileClick={onManageProfileClick}
        baseUrl={baseUrl}
        onMobileChromeCloseDrawer={handleMobileChromeCloseDrawer}
        notificationsPageActive={notificationsPageActive}
        messagesPageActive={messagesPageActive}
      />
    ) : (
      <div
        ref={shellHeaderRef}
        className="support-open-disc-shell-mobile-chrome"
        onClick={handleMobileChromeCloseDrawer}
      >
        {shellHeader}
      </div>
    )
  ) : null;

  useLayoutEffect(() => {
    if (!isMobileLayout) {
      document.documentElement.style.removeProperty('--od-mobile-drawer-pad-top');
      document.documentElement.style.removeProperty('--od-mobile-chrome-bottom');
      return undefined;
    }
    const el = shellHeaderRef.current;
    if (!el) return undefined;
    const sync = () => {
      const chromeBottom = Math.ceil(el.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--od-mobile-chrome-bottom', `${chromeBottom}px`);
      document.documentElement.style.setProperty('--od-mobile-drawer-pad-top', `${chromeBottom + 10}px`);
    };
    sync();
    const ro = createResizeObserver(sync);
    ro?.observe(el);
    window.addEventListener('resize', sync);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      vv?.removeEventListener('resize', sync);
      document.documentElement.style.removeProperty('--od-mobile-drawer-pad-top');
      document.documentElement.style.removeProperty('--od-mobile-chrome-bottom');
    };
  }, [isMobileLayout]);

  const isAppShell =
    appShell || Boolean(sidebar) || (Array.isArray(sidebarItems) && sidebarItems.length > 0);
  const shellRootClass = `support-open-disc${
    isAppShell ? ' support-open-disc--app-shell' : ' support-open-disc--home'
  }`;
  const layoutSectionClass = `posts-page-layout${
    isAppShell ? ' posts-page-layout--app-shell' : ''
  }${layoutClassName ? ` ${layoutClassName}` : ''}`;

  const mobileDrawerLayer =
    isMobileLayout && typeof document !== 'undefined' ? (
      <>
        {mobileDrawerOpen ? (
          <button
            type="button"
            className="support-open-disc-mobile-drawer-backdrop"
            aria-label="Close menu"
            tabIndex={-1}
            onClick={() => setMobileDrawerOpen(false)}
          />
        ) : null}
        {shellSidebar}
      </>
    ) : null;

  return (
    <section className={layoutSectionClass}>
      <div ref={shellRootRef} className={shellRootClass}>
        {!isMobileLayout ? shellHeader : mobileChrome}

        {mobileDrawerLayer ? createPortal(mobileDrawerLayer, document.body) : null}

        <div className={`support-open-disc-shell-layout${isNavCollapsed ? ' is-nav-collapsed' : ''}`}>
          {!isMobileLayout ? shellSidebar : null}
          <main className={`posts-page-layout__center${mainClassName !== 'posts-page-layout__center' ? ` ${mainClassName}` : ''}`}>
            {children}
          </main>
        </div>
      </div>
    </section>
  );
}
